/**
 * BlocksPage — orchard blocks: the harvest-management unit above plants.
 * A block is one variety/rootstock/age, tracked either AGGREGATE ("40 of this
 * variety", no per-tree rows) or INDIVIDUAL (real plant records). Sets default
 * harvest method (hand/machine/mixed) and season window (early/mid/late) that
 * harvest/pace/cost reports roll up by. Backed by /api/v1/blocks.
 *
 * Split view: the top half is the block table, the bottom half is a satellite
 * map. Clicking a block row (or a polygon on the map) selects it — the map
 * highlights + zooms to that block's outline, so it's obvious which block is
 * which. A block's outline comes from its linked map polygon (block.zone_id →
 * a plant_zone, or a legacy /map/zones farm_feature). Blocks with no polygon
 * yet are flagged "not on map" (draw them on the Farm Map).
 */
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Grid3x3, Hand, Cog, MapPin, QrCode } from 'lucide-react'
import { blocksApi, varietiesApi, locationsApi, farmMapApi, plantZonesApi, plantsApi, recordLink, PLANT_PURPOSES, BLOCK_CATEGORIES, type Block, type Plant, type Tag } from '../lib/api'
import { PlantModal } from '../components/shared/PlantModal'
import { QRLabelSheet, type QRLabelItem } from '../components/shared/QRLabelSheet'
import { useAccessibility } from '../lib/accessibility'
import { areaUnit, acresToDisplay, inputToAcres, formatArea, polygonAcres, spacingUnit, metersToDisplay, inputToMeters } from '../lib/units'
import { TagEditor, useTags } from '../components/shared/TagEditor'
import { useVineyardMode } from '../lib/useVineyardMode'

const C = {
  bg: 'var(--color-bg)', card: 'var(--color-bg-card)', border: 'rgba(255,255,255,0.08)',
  amber: 'var(--color-primary)', green: '#2D6A2F', greenLt: '#4CAF50', blue: '#5B8DEF',
  red: '#E5533C', text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
}
const windowColor: Record<string, string> = { early: C.greenLt, mid: C.amber, late: C.blue }
const blank: Partial<Block> = { name: '', tracking_mode: 'aggregate', harvest_method: 'hand', plant_count: 0 }

export function BlocksPage() {
  const qc = useQueryClient()
  const system = useAccessibility().prefs.measurementSystem // imperial → acres, metric → hectares
  const vineyard = useVineyardMode() // §55.20: relabel Variety→Vine variety + show trellis/spacing
  const [form, setForm] = useState<Partial<Block> | null>(null) // null = closed; has id = edit
  const [selectedId, setSelectedId] = useState<string | null>(null) // highlighted block (table ↔ map)
  const [qrItems, setQrItems] = useState<QRLabelItem[] | null>(null)
  const [plantModal, setPlantModal] = useState<Plant | null>(null) // shared editor — same as Plants page / map
  const qrItemFor = (b: Block): QRLabelItem => ({ url: recordLink('block', b.id), title: b.name, subtitle: b.variety_name || undefined })
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})

  const blocks = useQuery({ queryKey: ['blocks'], queryFn: () => blocksApi.list().then(r => r.data.blocks) })
  // All plants once → filter to the selected block's members client-side (the list
  // endpoint has no block filter; a farm's plant count is small enough for this).
  const plantsQ = useQuery({ queryKey: ['plants'], queryFn: () => plantsApi.list().then(r => r.data.plants) })
  const varieties = useQuery({ queryKey: ['varieties'], queryFn: () => varietiesApi.list().then(r => r.data.varieties) })
  const locations = useQuery({ queryKey: ['locations'], queryFn: () => locationsApi.list().then(r => r.data.locations) })
  // A block's outline can live in either store: the blocks-first map model backs
  // each block with a plant_zone (block.zone_id → plant_zones), but the form's
  // dropdown historically linked /map/zones farm_features. Merge BOTH so any
  // block's geometry resolves regardless of how it was linked.
  const mapZones = useQuery({ queryKey: ['map-zones'], queryFn: () => farmMapApi.listZones().then(r => r.data.zones) })
  const plantZones = useQuery({ queryKey: ['plant-zones'], queryFn: () => plantZonesApi.list().then(r => r.data) })

  const geomByZoneId = useMemo(() => {
    const m = new Map<string, any>()
    ;(mapZones.data ?? []).forEach((z: any) => { if (z.geometry) m.set(z.id, z.geometry) })
    ;(plantZones.data ?? []).forEach((z: any) => { if (z.geometry) m.set(z.id, z.geometry) }) // plant_zones win on id collision (canonical)
    return m
  }, [mapZones.data, plantZones.data])

  const geometryFor = useCallback((b: Block) => (b.zone_id ? geomByZoneId.get(b.zone_id) ?? null : null), [geomByZoneId])

  // ── Tags / labels (with hierarchy inheritance + filter) ──────────────────────
  const { tags, assignments } = useTags()
  const tagById = useMemo(() => new Map(tags.map(t => [t.id, t])), [tags])
  // direct tag-ids per (entity_type|entity_id)
  const directIds = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const a of assignments) {
      const k = `${a.entity_type}|${a.entity_id}`
      if (!m.has(k)) m.set(k, new Set())
      m.get(k)!.add(a.tag_id)
    }
    return m
  }, [assignments])
  const directFor = (type: string, id?: string | null) => (id ? directIds.get(`${type}|${id}`) ?? new Set<string>() : new Set<string>())
  // A block's EFFECTIVE tags = its own ∪ its plot's (location inheritance → multi-city).
  const effectiveBlockTagIds = useCallback((b: Block): Set<string> => {
    const out = new Set(directFor('block', b.id))
    for (const t of directFor('location', b.location_id)) out.add(t)
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directIds])
  const effectiveBlockTags = (b: Block): Tag[] => [...effectiveBlockTagIds(b)].map(id => tagById.get(id)).filter(Boolean) as Tag[]

  // Filter the block list (and the synced map) by selected tags (match-ANY).
  const [filterTags, setFilterTags] = useState<Set<string>>(new Set())
  const [showPlotTags, setShowPlotTags] = useState(false)
  const toggleFilter = (id: string) => setFilterTags(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const visibleBlocks = useMemo(() => {
    const list = blocks.data ?? []
    if (filterTags.size === 0) return list
    return list.filter(b => { const eff = effectiveBlockTagIds(b); return [...filterTags].some(id => eff.has(id)) })
  }, [blocks.data, filterTags, effectiveBlockTagIds])

  // Keep selection valid as the list changes; default to the first block.
  useEffect(() => {
    const list = blocks.data
    if (!list || list.length === 0) { if (selectedId) setSelectedId(null); return }
    if (!selectedId || !list.some(b => b.id === selectedId)) setSelectedId(list[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks.data]) // intentional — only react to list changes

  // When selection changes (e.g. from a map-polygon click), scroll its row into view.
  useEffect(() => {
    if (selectedId) rowRefs.current[selectedId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selectedId])

  // Inline "+ new location" so a multi-site farm can file a block without leaving the form.
  const [showNewLoc, setShowNewLoc] = useState(false)
  const [newLocName, setNewLocName] = useState('')
  const createLocation = useMutation({
    mutationFn: () => locationsApi.create({ name: newLocName.trim(), is_primary: (locations.data?.length ?? 0) === 0 }).then(r => r.data),
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['locations'] })
      set({ location_id: l.id }); setShowNewLoc(false); setNewLocName('')
    },
  })

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['blocks'] }); setForm(null) }
  const save = useMutation({
    mutationFn: (b: Partial<Block>) => b.id ? blocksApi.update(b.id, b) : blocksApi.create(b),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    // A block's map outline is its backing plant_zone (block.zone_id) — remove it
    // too, or the polygon lingers on the farm map as an orphaned standalone zone.
    mutationFn: async (b: Block) => {
      await blocksApi.remove(b.id)
      if (b.zone_id) await plantZonesApi.remove(b.zone_id)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['blocks'] }); qc.invalidateQueries({ queryKey: ['plant-zones'] }) },
  })

  // Draw a block's outline directly on the map below → create a plant_zone with that
  // geometry + link it to the block (block.zone_id). This is how a block created on
  // THIS page gets onto the farm map.
  const [drawingFor, setDrawingFor] = useState<string | null>(null)
  async function handlePolygon(geometry: any, ring: { lat: number; lng: number }[]) {
    const id = drawingFor
    setDrawingFor(null)
    if (!id) return
    const b = (blocks.data ?? []).find(x => x.id === id)
    if (!b) return
    try {
      const acres = polygonAcres(ring)
      const zone = await plantZonesApi.create({ name: b.name || 'Block', geometry, location_id: b.location_id ?? null })
      await blocksApi.update(id, { zone_id: zone.data.id, acreage: acres ? Math.round(acres * 100) / 100 : (b.acreage ?? null) })
      qc.invalidateQueries({ queryKey: ['plant-zones'] })
      qc.invalidateQueries({ queryKey: ['map-zones'] })
      qc.invalidateQueries({ queryKey: ['blocks'] })
      setSelectedId(id)
    } catch { /* non-fatal */ }
  }

  const set = (patch: Partial<Block>) => setForm(f => ({ ...(f ?? {}), ...patch }))

  const selectedBlock = blocks.data?.find(b => b.id === selectedId) ?? null
  const blockPlants = (plantsQ.data ?? []).filter(p => selectedId && p.block_id === selectedId)
  const selectedHasGeom = selectedBlock ? !!geometryFor(selectedBlock) : false
  const anyOnMap = (blocks.data ?? []).some(b => !!geometryFor(b))

  return (
    <main id="main-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: C.bg, color: C.text }}>
      {/* Top: header + (optional) form — fixed, never scrolls */}
      <div style={{ flexShrink: 0, padding: '24px 28px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, fontFamily: '"Syne", system-ui, sans-serif' }}>Blocks</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            {visibleBlocks.length > 0 && (
              <button onClick={() => setQrItems(visibleBlocks.map(qrItemFor))} title="Print QR stickers/signs for the listed blocks" style={btn(C.card, C.text)}>
                <QrCode size={14} /> QR labels
              </button>
            )}
            <button onClick={() => setForm(form ? null : { ...blank })} style={btn(C.amber, '#1a1300')}>
              <Plus size={14} /> New block
            </button>
          </div>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: C.faint }}>
          The harvest-management unit above plants — one variety, tracked in aggregate ("40 trees") or as individual plants.
          {' '}<span style={{ color: C.muted }}>Click a block to highlight it on the map below.</span>
        </p>

        {/* Tag filter bar — toggle tags to filter blocks (and the map). A block matches
            if ANY selected tag is on it OR inherited from its plot. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7, marginBottom: 12 }}>
          {tags.length > 0 && <span style={{ fontSize: 11, color: C.muted }}>Filter:</span>}
          {tags.map(t => {
            const active = filterTags.has(t.id)
            return (
              <button key={t.id} onClick={() => toggleFilter(t.id)} style={filterChip(t.color, active)} title={active ? 'Click to remove from filter' : 'Click to filter by this tag'}>
                {t.name}
              </button>
            )
          })}
          {filterTags.size > 0 && <button onClick={() => setFilterTags(new Set())} style={{ fontSize: 11, color: C.amber, background: 'none', border: 'none', cursor: 'pointer' }}>clear</button>}
          <button onClick={() => setShowPlotTags(v => !v)} style={{ marginLeft: 'auto', fontSize: 11, color: C.muted, background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '3px 9px', cursor: 'pointer' }}>
            {showPlotTags ? 'Hide plot tags' : '🏷 Tag plots (city, region…)'}
          </button>
        </div>

        {/* Plot tags — tag a whole plot/parcel (e.g. a city); every block in it inherits.
            This is how multi-city farms filter + report by location. */}
        {showPlotTags && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <p style={{ margin: '0 0 8px', fontSize: 11, color: C.muted }}>
              Tags on a plot are inherited by every block inside it — tag a plot “Fort Myers”, then filter by it. Manage plots on the Farm Map.
            </p>
            {(locations.data ?? []).length === 0 ? (
              <span style={{ fontSize: 12, color: C.faint }}>No plots yet. Add one on the Farm Map.</span>
            ) : (locations.data ?? []).map(l => (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: C.text, minWidth: 130 }}><MapPin size={12} style={{ verticalAlign: -2, marginRight: 4, color: C.faint }} />{l.name}{l.is_primary ? ' (primary)' : ''}</span>
                <TagEditor entityType="location" entityId={l.id} />
              </div>
            ))}
          </div>
        )}

        {/* Create / edit form */}
        {form && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 14, maxHeight: '46vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600 }}>{form.id ? 'Edit block' : 'New block'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <Field label="Name *"><input value={form.name ?? ''} onChange={e => set({ name: e.target.value })} style={input} placeholder="Block 3" /></Field>
              <Field label={vineyard ? 'Vine variety' : 'Variety'}>
                <select value={form.variety_id ?? ''} onChange={e => set({ variety_id: e.target.value || null })} style={input}>
                  <option value="">—</option>
                  {(varieties.data ?? []).map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </Field>
              <Field label="Rootstock"><input value={form.rootstock ?? ''} onChange={e => set({ rootstock: e.target.value })} style={input} /></Field>

              {vineyard && <>
                <Field label="Trellis type">
                  <input value={form.trellis_type ?? ''} onChange={e => set({ trellis_type: e.target.value })} style={input} placeholder="VSP, cordon, …" />
                </Field>
                <Field label={`Row spacing (${spacingUnit(system)})`}><input type="number" step="0.1"
                  value={form.row_spacing_m != null ? Number(metersToDisplay(form.row_spacing_m, system).toFixed(2)) : ''}
                  onChange={e => set({ row_spacing_m: e.target.value ? inputToMeters(Number(e.target.value), system) : null })}
                  style={input} /></Field>
                <Field label={`Vine spacing (${spacingUnit(system)})`}><input type="number" step="0.1"
                  value={form.vine_spacing_m != null ? Number(metersToDisplay(form.vine_spacing_m, system).toFixed(2)) : ''}
                  onChange={e => set({ vine_spacing_m: e.target.value ? inputToMeters(Number(e.target.value), system) : null })}
                  style={input} /></Field>
              </>}

              <Field label="Tracking">
                <select value={form.tracking_mode} onChange={e => set({ tracking_mode: e.target.value as Block['tracking_mode'] })} style={input}>
                  <option value="aggregate">Aggregate (count only)</option>
                  <option value="individual">Individual plants</option>
                </select>
              </Field>
              {form.tracking_mode === 'aggregate'
                ? <Field label="Plant count"><input type="number" value={form.plant_count ?? 0} onChange={e => set({ plant_count: Number(e.target.value) })} style={input} /></Field>
                : <Field label="Plants"><div style={{ ...input, color: C.faint, paddingTop: 8 }}>Tracked individually →</div></Field>}
              <Field label={`Area (${areaUnit(system)})`}><input type="number" step="0.01"
                value={form.acreage != null ? Number(acresToDisplay(form.acreage, system).toFixed(2)) : ''}
                onChange={e => set({ acreage: e.target.value ? inputToAcres(Number(e.target.value), system) : null })}
                style={input} /></Field>

              <Field label="Harvest method">
                <select value={form.harvest_method} onChange={e => set({ harvest_method: e.target.value as Block['harvest_method'] })} style={input}>
                  <option value="hand">Hand</option><option value="machine">Machine</option><option value="mixed">Mixed</option>
                </select>
              </Field>
              <Field label="Season window">
                <select value={form.harvest_window ?? ''} onChange={e => set({ harvest_window: (e.target.value || null) as Block['harvest_window'] })} style={input}>
                  <option value="">—</option><option value="early">Early</option><option value="mid">Mid</option><option value="late">Late</option>
                </select>
              </Field>
              <Field label="Planted year"><input type="number" value={form.planted_year ?? ''} onChange={e => set({ planted_year: e.target.value ? Number(e.target.value) : null })} style={input} /></Field>

              <Field label="Location">
                {showNewLoc ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input value={newLocName} onChange={e => setNewLocName(e.target.value)} style={input} placeholder="Home Grove" />
                    <button type="button" disabled={!newLocName.trim() || createLocation.isPending} onClick={() => createLocation.mutate()} style={btn(C.green, '#fff')}>{createLocation.isPending ? '…' : 'Add'}</button>
                    <button type="button" onClick={() => { setShowNewLoc(false); setNewLocName('') }} style={btn(C.card, C.muted)}>✕</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select value={form.location_id ?? ''} onChange={e => set({ location_id: e.target.value || null })} style={input}>
                      <option value="">—</option>
                      {(locations.data ?? []).map(l => <option key={l.id} value={l.id}>{l.name}{l.is_primary ? ' (primary)' : ''}</option>)}
                    </select>
                    <button type="button" onClick={() => setShowNewLoc(true)} style={btn(C.card, C.amber)}>+ new</button>
                  </div>
                )}
              </Field>

              <Field label="Map outline (geometry)">
                <select value={form.zone_id ?? ''} onChange={e => set({ zone_id: e.target.value || null })} style={input}>
                  <option value="">— not on map —</option>
                  {(plantZones.data ?? []).map((z: any) => <option key={z.id} value={z.id}>{z.name}</option>)}
                  {(mapZones.data ?? []).map((z: any) => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </Field>

              <Field label="Purpose (inherited by plants)">
                <select value={form.purpose ?? ''} onChange={e => set({ purpose: e.target.value })} style={input}>
                  {PLANT_PURPOSES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>

              <Field label="Category">
                <select value={form.category ?? 'production'} onChange={e => set({ category: e.target.value as Block['category'] })} style={input}>
                  {BLOCK_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
            </div>
            <div style={{ marginTop: 12 }}>
              <Field label="Notes"><input value={form.notes ?? ''} onChange={e => set({ notes: e.target.value })} style={{ ...input, width: '100%' }} /></Field>
            </div>
            {form.id ? (
              <div style={{ marginTop: 12 }}>
                <Field label="Tags (inherited by plants in this block)"><div style={{ paddingTop: 2 }}><TagEditor entityType="block" entityId={form.id} /></div></Field>
              </div>
            ) : (
              <p style={{ fontSize: 11, color: C.faint, marginTop: 12 }}>Save the block to add tags.</p>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button disabled={!form.name || save.isPending} onClick={() => save.mutate(form)} style={btn(C.green, '#fff')}>{save.isPending ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setForm(null)} style={btn(C.card, C.text)}>Cancel</button>
              {save.isError && <span style={{ color: C.red, fontSize: 12, alignSelf: 'center' }}>{(save.error as any)?.response?.data?.error ?? 'Save failed'}</span>}
            </div>
          </div>
        )}
      </div>

      {/* Split: table (top) + map (bottom). Each takes ~half the remaining height. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 28px 24px' }}>
        {/* Table */}
        <div style={{ flex: '1 1 46%', minHeight: 130, overflow: 'auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 4 }}>
          {(blocks.data?.length ?? 0) === 0 ? (
            <p style={{ color: C.faint, fontSize: 13, padding: 16, margin: 0 }}>No blocks yet. Create one to start tracking harvest by block.</p>
          ) : visibleBlocks.length === 0 ? (
            <p style={{ color: C.faint, fontSize: 13, padding: 16, margin: 0 }}>No blocks match the selected tag filter. <button onClick={() => setFilterTags(new Set())} style={{ background: 'none', border: 'none', color: C.amber, cursor: 'pointer', fontSize: 13 }}>Clear filter</button></p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ color: C.muted, textAlign: 'left', fontSize: 11 }}>
                <th style={th}>Block</th><th style={th}>{vineyard ? 'Vine variety' : 'Variety'}</th><th style={th}>Tracking</th>
                <th style={thR}>Plants</th><th style={th}>Method</th><th style={th}>Window</th><th style={thR}>{areaUnit(system) === 'ac' ? 'Acres' : 'Hectares'}</th><th style={thR}></th>
              </tr></thead>
              <tbody>
                {visibleBlocks.map(b => {
                  const onMap = !!geometryFor(b)
                  const sel = b.id === selectedId
                  return (
                    <tr key={b.id}
                      ref={el => { rowRefs.current[b.id] = el }}
                      onClick={() => setSelectedId(b.id)}
                      style={{
                        borderTop: `1px solid ${C.border}`, cursor: 'pointer',
                        background: sel ? 'color-mix(in srgb, var(--color-primary) 14%, transparent)' : 'transparent',
                        boxShadow: sel ? `inset 3px 0 0 ${C.amber}` : 'none',
                      }}>
                      <td style={td}>
                        <span title={onMap ? 'On the map' : 'Not on the map yet'} aria-hidden style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: 8, marginRight: 8, verticalAlign: 1,
                          background: onMap ? C.amber : 'transparent', border: onMap ? 'none' : `1.5px solid ${C.faint}`,
                        }} />
                        <Grid3x3 size={13} style={{ color: C.faint, verticalAlign: -2, marginRight: 6 }} />{b.name}
                        {b.category === 'support' && <span style={{ ...chip(C.greenLt), marginLeft: 6 }} title="Ecosystem support — not a production crop">support</span>}
                        {b.category === 'mixed' && <span style={{ ...chip(C.blue), marginLeft: 6 }} title="Mixed crop + support">mixed</span>}
                        {(() => {
                          const eff = effectiveBlockTags(b)
                          if (eff.length === 0) return null
                          const direct = directFor('block', b.id)
                          return (
                            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 3, marginLeft: 6 }}>
                              {eff.map(t => (
                                <span key={t.id} style={{ ...chip(t.color), ...(direct.has(t.id) ? {} : { opacity: 0.7, borderStyle: 'dashed' }) }}
                                  title={direct.has(t.id) ? t.name : `${t.name} (inherited from plot)`}>{t.name}</span>
                              ))}
                            </span>
                          )
                        })()}
                      </td>
                      <td style={td}>
                        {b.composition && b.composition.length > 0 ? (
                          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                            {b.composition.map((c, i) => (
                              <span key={c.variety_id ?? `v${i}`} style={chip(C.blue)} title={c.category || undefined}>
                                {c.variety_name} ×{c.plant_count}
                              </span>
                            ))}
                          </span>
                        ) : (
                          b.variety_name ?? <span style={{ color: C.faint }}>—</span>
                        )}
                      </td>
                      <td style={td}><span style={{ color: b.tracking_mode === 'individual' ? C.blue : C.muted }}>{b.tracking_mode}</span></td>
                      <td style={tdR}>{b.effective_plant_count}</td>
                      <td style={td}>{b.harvest_method === 'machine' ? <Cog size={13} style={{ verticalAlign: -2 }} /> : <Hand size={13} style={{ verticalAlign: -2 }} />} {b.harvest_method}</td>
                      <td style={td}>{b.harvest_window ? <span style={chip(windowColor[b.harvest_window])}>{b.harvest_window}</span> : <span style={{ color: C.faint }}>—</span>}</td>
                      <td style={tdR}>{b.acreage != null ? formatArea(b.acreage, system) : '—'}</td>
                      <td style={{ ...tdR, whiteSpace: 'nowrap' }}>
                        <QrCode size={14} style={{ cursor: 'pointer', color: C.muted, marginRight: 12 }} aria-label="Print QR" onClick={e => { e.stopPropagation(); setQrItems([qrItemFor(b)]) }} />
                        <Pencil size={14} style={{ cursor: 'pointer', color: C.muted, marginRight: 12 }} onClick={e => { e.stopPropagation(); setForm(b) }} />
                        <Trash2 size={14} style={{ cursor: 'pointer', color: C.faint }} onClick={e => { e.stopPropagation(); if (confirm(`Delete block “${b.name}”? Its trees and plants are kept; the outline is removed from the map.`)) remove.mutate(b) }} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Map */}
        <div style={{ flex: '1 1 54%', minHeight: 240, position: 'relative', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <BlocksMap blocks={visibleBlocks} geometryFor={geometryFor} selectedId={selectedId} onSelect={setSelectedId} drawMode={!!drawingFor} onPolygon={handlePolygon} />

          {/* Draw-outline control: a selected block with no polygon can be drawn onto
              the map (creates its geometry → shows on the farm map too). */}
          {selectedBlock && (!selectedHasGeom || drawingFor === selectedBlock.id) && (
            <button
              onClick={() => setDrawingFor(drawingFor ? null : selectedBlock.id)}
              style={{ position: 'absolute', top: 10, right: 10, zIndex: 600, fontSize: 11, fontWeight: 600, cursor: 'pointer', borderRadius: 8, padding: '5px 10px',
                background: drawingFor ? C.red : 'color-mix(in srgb, var(--color-primary) 18%, var(--color-bg))',
                border: `1px solid ${drawingFor ? C.red : 'color-mix(in srgb, var(--color-primary) 50%, transparent)'}`,
                color: drawingFor ? '#fff' : C.amber }}
            >
              {drawingFor ? '✕ Cancel drawing' : `✏️ Draw “${selectedBlock.name}” outline`}
            </button>
          )}
          {drawingFor && (
            <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 600, pointerEvents: 'none',
              fontSize: 11.5, color: C.text, background: 'color-mix(in srgb, var(--color-bg) 85%, transparent)', border: `1px solid ${C.border}`, borderRadius: 10, padding: '7px 12px', textAlign: 'center' }}>
              Click to add corners; click the first point (or double-click) to finish.
            </div>
          )}

          {/* Caption */}
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 500, fontSize: 11, fontWeight: 600, color: C.text,
            background: 'color-mix(in srgb, var(--color-bg) 72%, transparent)', border: `1px solid ${C.border}`,
            borderRadius: 8, padding: '4px 9px', display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none' }}>
            <MapPin size={12} style={{ color: C.amber }} />
            {selectedBlock ? selectedBlock.name : 'Block map'}
          </div>

          {/* Hints */}
          {!anyOnMap && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, pointerEvents: 'none' }}>
              <div style={{ maxWidth: 320, textAlign: 'center', fontSize: 12, color: C.text, lineHeight: 1.5,
                background: 'color-mix(in srgb, var(--color-bg) 82%, transparent)', border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 18px' }}>
                No blocks are on the map yet. Draw a block on the <strong>Farm Map</strong> (or link a map outline in a block's form) and it will appear here.
              </div>
            </div>
          )}
          {anyOnMap && selectedBlock && !selectedHasGeom && (
            <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 500, pointerEvents: 'none',
              fontSize: 11.5, color: C.text, background: 'color-mix(in srgb, var(--color-bg) 82%, transparent)',
              border: `1px solid ${C.border}`, borderRadius: 10, padding: '7px 12px', textAlign: 'center', maxWidth: '90%' }}>
              <strong>{selectedBlock.name}</strong> isn't on the map yet — draw it on the Farm Map to see its outline here.
            </div>
          )}
        </div>

        {/* Plants in the selected block — click any to open the SAME plant editor
            used on the Plants page and the farm map (one modal everywhere). */}
        {selectedBlock && (
          <div style={{ flex: '0 0 auto', maxHeight: 180, overflow: 'auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '10px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>
              Plants in “{selectedBlock.name}” <span style={{ color: C.muted, fontWeight: 400 }}>({blockPlants.length})</span>
            </div>
            {blockPlants.length === 0 ? (
              <p style={{ fontSize: 12, color: C.faint, margin: 0, lineHeight: 1.5 }}>
                No individual plants linked to this block yet. Place or detect trees inside it on the Farm Map, or assign existing plants — they'll auto-add to the block they sit in.
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {blockPlants.map(p => (
                  <button key={p.id} onClick={() => setPlantModal(p)} title="Open plant details"
                    style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 8, padding: '4px 10px', background: 'var(--color-bg)', border: `1px solid ${C.border}`, color: C.text }}>
                    {p.label}{p.variety?.name ? <span style={{ color: C.muted, fontWeight: 400 }}> · {p.variety.name}</span> : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {plantModal && <PlantModal plant={plantModal} onClose={() => setPlantModal(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['plants'] }); qc.invalidateQueries({ queryKey: ['blocks'] }) }} />}
      {qrItems && <QRLabelSheet heading="Block QR labels" action="Scan to record block picking / activity" items={qrItems} onClose={() => setQrItems(null)} />}
    </main>
  )
}

// ── Synced satellite map of block outlines ────────────────────────────────────
// Reuses FarmMapPage's Leaflet/Esri pattern (raw Leaflet via dynamic import, the
// keyless Esri World Imagery basemap). Renders each block's polygon; clicking one
// selects it (→ parent), and changing the selection highlights + zooms to it.
function BlocksMap({ blocks, geometryFor, selectedId, onSelect, drawMode, onPolygon }: {
  blocks: Block[]
  geometryFor: (b: Block) => any | null
  selectedId: string | null
  onSelect: (id: string) => void
  drawMode?: boolean
  onPolygon?: (geometry: any, ring: { lat: number; lng: number }[]) => void
}) {
  const elRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const groupRef = useRef<any>(null)
  const layersRef = useRef<Record<string, any>>({})
  const drawerRef = useRef<any>(null)
  const framedRef = useRef(false) // only auto-frame all blocks once
  const onSelectRef = useRef(onSelect)
  const onPolygonRef = useRef(onPolygon)
  useEffect(() => { onSelectRef.current = onSelect }, [onSelect])
  useEffect(() => { onPolygonRef.current = onPolygon }, [onPolygon])

  // Init the map exactly once.
  useEffect(() => {
    if (!elRef.current || mapRef.current) return
    let cancelled = false
    import('leaflet').then(async (mod) => {
      if (cancelled || !elRef.current || mapRef.current) return
      const L: any = (mod as any).default ?? mod
      ;(window as any).L = L
      await import('leaflet-draw') // adds L.Draw (used for "draw this block's outline")
      const readJSON = (k: string) => { try { const r = localStorage.getItem(k); return r ? JSON.parse(r) : null } catch { return null } }
      const sv = readJSON('grafty-farm-view')          // the farmer's saved farm-map view
      const loc = readJSON('grafty-farm-location')      // geocoded farm location
      const center: [number, number] = sv ? [sv.lat, sv.lng] : loc ? [loc.lat, loc.lng] : [26.45, -81.8]
      const map = L.map(elRef.current, { center, zoom: sv ? sv.zoom : 16, zoomControl: false })
      L.control.zoom({ position: 'bottomright' }).addTo(map)
      const TRANSPARENT = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        { attribution: '© Esri, Maxar, Earthstar Geographics', maxZoom: 22, maxNativeZoom: 19, crossOrigin: '', errorTileUrl: TRANSPARENT }).addTo(map)
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        { maxZoom: 22, opacity: 0.6, crossOrigin: '', errorTileUrl: TRANSPARENT }).addTo(map)
      const g = new L.FeatureGroup(); g.addTo(map); groupRef.current = g
      mapRef.current = map
      // Container starts 0-sized inside the flex split — recompute on next frame and
      // on every resize so the whole pane fills with tiles.
      setTimeout(() => map.invalidateSize(), 0)
      try { const ro = new ResizeObserver(() => map.invalidateSize()); ro.observe(elRef.current); (map as any)._ro = ro } catch { /* no ResizeObserver */ }
    })
    return () => {
      cancelled = true
      const m = mapRef.current
      if (m) { try { (m as any)._ro?.disconnect?.() } catch { /* noop */ } m.remove(); mapRef.current = null }
    }
  }, [])

  // (Re)draw polygons when the blocks or their geometry change.
  useEffect(() => {
    const map = mapRef.current, g = groupRef.current
    if (!map || !g) return
    import('leaflet').then((mod) => {
      const L: any = (mod as any).default ?? mod
      g.clearLayers(); layersRef.current = {}
      let hasAny = false
      blocks.forEach(b => {
        const geom = geometryFor(b)
        if (!geom) return
        hasAny = true
        const layer = L.geoJSON(geom, { style: polyStyle(b.id === selectedId) })
        layer.on('click', () => onSelectRef.current(b.id))
        // Leaflet renders string tooltip content as innerHTML — escape the block name (stored XSS).
        const safeName = String(b.name ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string))
        try { layer.bindTooltip(safeName, { permanent: true, direction: 'center', className: 'block-map-label' }) } catch { /* tooltip optional */ }
        layer.addTo(g)
        layersRef.current[b.id] = layer
      })
      // Frame all block outlines once on first load (don't fight the selection zoom after).
      if (hasAny && !framedRef.current) {
        framedRef.current = true
        try { const bnds = g.getBounds(); if (bnds.isValid()) map.fitBounds(bnds, { padding: [40, 40], maxZoom: 19 }) } catch { /* noop */ }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, geometryFor])

  // Highlight + zoom to the selected block.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    Object.entries(layersRef.current).forEach(([id, layer]) => {
      const sel = id === selectedId
      try { layer.setStyle(polyStyle(sel)) } catch { /* noop */ }
      if (sel) { try { layer.bringToFront() } catch { /* noop */ } }
    })
    const selLayer = selectedId ? layersRef.current[selectedId] : null
    if (selLayer) {
      try { const b = selLayer.getBounds(); if (b.isValid()) map.fitBounds(b, { padding: [60, 60], maxZoom: 20 }) } catch { /* noop */ }
    }
  }, [selectedId])

  // Draw mode — activate the leaflet-draw polygon tool; on completion hand the
  // geometry + ring back to the page (which creates the zone + links the block).
  useEffect(() => {
    const map = mapRef.current
    const L = (window as any).L
    if (!map || !L?.Draw || !drawMode) return
    let drawer: any
    try { drawer = new L.Draw.Polygon(map, { allowIntersection: false, shapeOptions: { color: '#F5A623', weight: 2 } }); drawer.enable(); drawerRef.current = drawer } catch { /* noop */ }
    const onCreated = (e: any) => {
      try {
        const ring = (e.layer.getLatLngs?.()?.[0] ?? []).map((p: any) => ({ lat: p.lat, lng: p.lng }))
        const geo = e.layer.toGeoJSON()
        onPolygonRef.current?.(geo.geometry, ring)
      } catch { /* noop */ }
      try { drawerRef.current?.disable() } catch { /* noop */ }
    }
    map.on(L.Draw.Event.CREATED, onCreated)
    return () => { try { drawerRef.current?.disable() } catch { /* noop */ }; map.off(L.Draw.Event.CREATED, onCreated) }
  }, [drawMode])

  return <div ref={elRef} style={{ position: 'absolute', inset: 0 }} />
}

// Leaflet needs real hex (not CSS vars) for SVG stroke/fill — keep the amber brand hue.
const polyStyle = (selected: boolean) => ({
  color: selected ? '#FFC53D' : '#F5A623',
  weight: selected ? 4 : 2,
  fillColor: '#F5A623',
  fillOpacity: selected ? 0.35 : 0.12,
})

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block', fontSize: 11, color: C.muted }}>{label}<div style={{ marginTop: 4 }}>{children}</div></label>
}
const btn = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, background: bg, color: fg,
  border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
})
const chip = (color: string): React.CSSProperties => ({
  display: 'inline-block', fontSize: 10, fontWeight: 600, color, background: 'var(--color-bg-hover)',
  border: `1px solid ${color}33`, borderRadius: 100, padding: '2px 8px',
})
const filterChip = (color: string, active: boolean): React.CSSProperties => ({
  fontSize: 11, fontWeight: 600, cursor: 'pointer', borderRadius: 100, padding: '2px 10px',
  color: active ? '#fff' : color,
  background: active ? color : `color-mix(in srgb, ${color} 14%, transparent)`,
  border: `1px solid ${active ? color : `color-mix(in srgb, ${color} 45%, transparent)`}`,
})
const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 9px', fontSize: 13 }
const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 600 }
const thR: React.CSSProperties = { ...th, textAlign: 'right' }
const td: React.CSSProperties = { padding: '9px 10px' }
const tdR: React.CSSProperties = { ...td, textAlign: 'right' }

export default BlocksPage
